// src/modules/assetManagement/management/UploadLog.tsx
// Log of failed upload attempts: one row per attempt, with retry, escalate, dismiss and CSV export.
import React, { useMemo, useState } from "react";
import { Download, Headphones, History, RotateCcw, XCircle } from "lucide-react";
import { assetApi } from "../assetApi";
import { SupportModal } from "../components/AssetModals";
import { Button, EmptyState, SearchInput, Select } from "../components/ui";
import type { AttemptResolution, UploadAttemptLog, UploadStage } from "../types";
import { useAssetQuery } from "../useAssetQuery";
import { formatBytes, formatDate, MAX_ATTEMPTS } from "../utils";
import type { useUploadQueue } from "./useUploadQueue";

type Queue = ReturnType<typeof useUploadQueue>;

const STAGE_META: Record<UploadStage, { label: string; className: string }> = {
  validation: { label: "Validation", className: "bg-orange-50 text-orange-700 border-orange-200" },
  upload: { label: "Upload", className: "bg-red-50 text-red-700 border-red-200" },
  processing: { label: "Processing", className: "bg-purple-50 text-purple-700 border-purple-200" },
  linking: { label: "Linking", className: "bg-slate-100 text-slate-700 border-slate-200" },
};

const RESOLUTION_META: Record<AttemptResolution, { label: string; className: string }> = {
  open: { label: "Open", className: "text-red-600" },
  retried_ok: { label: "Retried OK", className: "text-emerald-600" },
  dismissed: { label: "Dismissed", className: "text-slate-400" },
  ticket_raised: { label: "Ticket", className: "text-blue-600" },
};

const toCsv = (rows: UploadAttemptLog[]) => {
  const head = ["Time", "File", "Size", "Attempt", "Stage", "Error code", "Error", "User", "Status", "Ticket"];
  const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = rows.map((r) =>
    [r.at, r.fileName, formatBytes(r.sizeBytes), r.attempt, r.stage, r.errorCode, r.errorMessage, r.user, r.resolution, r.ticketRef ?? ""]
      .map(esc)
      .join(","),
  );
  return [head.join(","), ...lines].join("\n");
};

const Stat: React.FC<{ label: string; value: number; tone?: string }> = ({ label, value, tone = "text-slate-900" }) => (
  <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-2xl font-bold mt-0.5 ${tone}`}>{value}</p>
  </div>
);

const UploadLog: React.FC<{ queue: Queue; onRetried: () => void }> = ({ queue, onRetried }) => {
  const { data: rows = [], initialLoading } = useAssetQuery(() => assetApi.listUploadLog(), []);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("all");
  const [resolution, setResolution] = useState("all");
  const [supportRow, setSupportRow] = useState<UploadAttemptLog | null>(null);

  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!search || r.fileName.toLowerCase().includes(search.toLowerCase())) &&
          (stage === "all" || r.stage === stage) &&
          (resolution === "all" || r.resolution === resolution),
      ),
    [rows, search, stage, resolution],
  );

  const files = new Set(rows.map((r) => r.fileKey));
  const openFiles = new Set(rows.filter((r) => r.resolution === "open").map((r) => r.fileKey));
  const tickets = new Set(rows.filter((r) => r.ticketRef).map((r) => r.ticketRef));
  // Only the latest attempt row for a file offers actions
  const latestByFile = new Map<string, string>();
  rows.forEach((r) => {
    if (!latestByFile.has(r.fileKey)) latestByFile.set(r.fileKey, r.id);
  });

  const exportCsv = () => {
    const blob = new Blob([toCsv(visible)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `upload-failures-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Failed attempts" value={rows.length} />
        <Stat label="Files affected" value={files.size} />
        <Stat label="Still open" value={openFiles.size} tone={openFiles.size ? "text-red-600" : "text-slate-900"} />
        <Stat label="Tickets raised" value={tickets.size} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search file name" />
        <Select
          ariaLabel="Filter by stage"
          value={stage}
          onChange={setStage}
          options={[{ value: "all", label: "All stages" }, ...Object.entries(STAGE_META).map(([v, m]) => ({ value: v, label: m.label }))]}
        />
        <Select
          ariaLabel="Filter by status"
          value={resolution}
          onChange={setResolution}
          options={[{ value: "all", label: "All statuses" }, ...Object.entries(RESOLUTION_META).map(([v, m]) => ({ value: v, label: m.label }))]}
        />
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => void assetApi.clearResolvedLog()} disabled={rows.every((r) => r.resolution === "open")}>
            Clear resolved
          </Button>
          <Button size="sm" icon={<Download size={14} />} onClick={exportCsv} disabled={visible.length === 0}>
            Export CSV
          </Button>
        </div>
      </div>

      {initialLoading ? (
        <div className="h-64 rounded-xl bg-slate-200/60 animate-pulse" />
      ) : visible.length === 0 ? (
        <EmptyState icon={<History size={22} />} title="No failed attempts" hint="Every failed upload attempt is recorded here." />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">File</th>
                <th className="text-left px-4 py-3 font-semibold">Attempt</th>
                <th className="text-left px-4 py-3 font-semibold">Stage</th>
                <th className="text-left px-4 py-3 font-semibold">Error</th>
                <th className="text-left px-4 py-3 font-semibold">By</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((r) => {
                const queued = queue.items.find((i) => i.key === r.fileKey);
                const isLatest = latestByFile.get(r.fileKey) === r.id;
                const canRetry = isLatest && r.resolution === "open" && queued?.status === "failed" && queued.attempts < MAX_ATTEMPTS;
                const res = RESOLUTION_META[r.resolution];
                return (
                  <tr key={r.id} className="align-top hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(r.at, true)}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800 truncate max-w-[240px]" title={r.fileName}>{r.fileName}</p>
                      <p className="text-xs text-slate-500">{formatBytes(r.sizeBytes)}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.attempt} of {MAX_ATTEMPTS}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${STAGE_META[r.stage].className}`}>
                        {STAGE_META[r.stage].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[300px]">
                      <p className="font-mono text-[11px] text-slate-500">{r.errorCode}</p>
                      <p className="text-slate-700">{r.errorMessage}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs truncate max-w-[160px]">{r.user}</td>
                    <td className={`px-4 py-3 text-xs font-semibold whitespace-nowrap ${res.className}`}>
                      {res.label}
                      {r.ticketRef ? ` ${r.ticketRef}` : ""}
                    </td>
                    <td className="px-4 py-3">
                      {isLatest && r.resolution === "open" && (
                        <div className="flex justify-end gap-1">
                          {canRetry && (
                            <Button
                              size="sm"
                              icon={<RotateCcw size={13} />}
                              onClick={() => {
                                queue.retry(r.fileKey);
                                onRetried();
                              }}
                            >
                              Retry
                            </Button>
                          )}
                          <Button size="sm" icon={<Headphones size={13} />} onClick={() => setSupportRow(r)}>
                            Support
                          </Button>
                          <button
                            type="button"
                            title="Dismiss"
                            aria-label={`Dismiss failures for ${r.fileName}`}
                            onClick={() => void assetApi.resolveFileAttempts(r.fileKey, "dismissed")}
                            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {supportRow && (
        <SupportModal
          fileName={supportRow.fileName}
          defaultCategory="Upload failed"
          defaultDescription={`Upload of ${supportRow.fileName} (${formatBytes(supportRow.sizeBytes)}) failed ${supportRow.attempt} time${
            supportRow.attempt === 1 ? "" : "s"
          }.\nLast error: ${supportRow.errorCode} – ${supportRow.errorMessage}\nStage: ${supportRow.stage}\n\n`}
          onClose={() => setSupportRow(null)}
          onRaised={(t) => {
            queue.markTicket(supportRow.fileKey, t.ref);
            void assetApi.resolveFileAttempts(supportRow.fileKey, "ticket_raised", t.ref);
          }}
        />
      )}
    </div>
  );
};

export default UploadLog;
